import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Compass, Users, Brain, CheckSquare, ArrowRight, Clock, Cpu, Briefcase } from 'lucide-react';
import Navbar from '../components/Navbar';
import PathwayHero from '../components/PathwayHero';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { after10thData } from '../data/pathwaysMock';
import LexiTrigger from '../components/lexi/LexiTrigger';
import { LEXI_ENABLED } from '../lib/features';

const After10thPathway = () => {
  const [selectedPathway, setSelectedPathway] = useState(null);

  const testPathways = [
    { id: 'btech',    title: 'B.Tech',              subtitle: 'Engineering, CS, Electronics, Mechanical & more', icon: Cpu,       color: 'teal', route: '/career-clarity/btech' },
    { id: 'bba-mba',  title: 'Management (BBA/MBA)', subtitle: 'Marketing, Finance, HR, Operations & more',       icon: Briefcase, color: 'blue', route: '/career-clarity/bba-mba' },
  ];

  const journeyStages = (pathwayId) => ([
    { id: 1, title: 'Test 1: Interest Discovery',                            description: 'Find your natural pull across all branches',                                icon: Compass,     duration: '4-6 mins', questions: 15 },
    { id: 2, title: pathwayId === 'btech' ? 'Test 2: Branch Awareness'   : 'Test 2: Branch Awareness',          description: 'Match your style to each branch/specialization',                          icon: Users,       duration: '4-6 mins', questions: 15 },
    { id: 3, title: pathwayId === 'btech' ? 'Test 3: Skill & Learning Style' : 'Test 3: Skill & Personality',   description: 'Confirm how you learn and what you enjoy daily',                          icon: Brain,       duration: '4-6 mins', questions: 15 },
    { id: 4, title: pathwayId === 'btech' ? 'Test 4: AI-Ready Future Engineering' : 'Test 4: AI-Ready Business Career', description: 'Find where AI, agentic AI & your branch meet',                  icon: CheckSquare, duration: '4-6 mins', questions: 15 },
  ]);

  return (
    <div className="min-h-screen bg-white">
      {/* Navbar */}
      <Navbar />
      {/* Hero */}
      <PathwayHero {...after10thData.hero} />

      {/* Lexi quick-answer CTA */}
      {LEXI_ENABLED && <section className="px-4 md:px-6 py-6 bg-gradient-to-br from-teal-50/50 via-white to-blue-50/50">
        <div className="max-w-3xl mx-auto">
          <LexiTrigger
            variant="card"
            prefill="I'm in 10th class. MPC vs BiPC vs MEC vs HEC — which stream suits me? Just talk to me, ask me a few questions, give one honest recommendation."
            label="MPC, BiPC, MEC, HEC — which suits me?"
            description="Skip the 4-test journey. Talk to Lexi in 2 minutes."
            testId="after10th-lexi-trigger"
          />
        </div>
      </section>

      }
      {/* 4 Tests (replacing the Fit Test) */}
      <section id="tests" className="py-16 px-4 md:px-6 bg-gradient-to-br from-teal-50 via-white to-blue-50" data-testid="four-tests-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <Badge className="bg-teal-100 text-teal-700 mb-3">Step 1 of your journey</Badge>
            <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-3">Career Clarity in 4 Short Tests</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">4 tests · 15 questions each · theory after every test · final career clarity report at the end. Pick your stream to start the journey.</p>
          </div>

          {!selectedPathway ? (
            <div className="grid md:grid-cols-2 gap-5 max-w-3xl mx-auto" data-testid="pathway-selector">
              {testPathways.map((p) => {
                const Icon = p.icon;
                return (
                  <button key={p.id} data-testid={`select-pathway-${p.id}`} onClick={() => setSelectedPathway(p.id)}
                    className={`p-6 bg-white rounded-2xl border-2 text-left transition-all hover:shadow-lg hover:-translate-y-1 ${p.color === 'teal' ? 'border-teal-200 hover:border-teal-400' : 'border-blue-200 hover:border-blue-400'}`}>
                    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-4 ${p.color === 'teal' ? 'bg-teal-100' : 'bg-blue-100'}`}>
                      <Icon className={p.color === 'teal' ? 'text-teal-600' : 'text-blue-600'} size={28} />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-1">{p.title}</h3>
                    <p className="text-sm text-gray-600">{p.subtitle}</p>
                  </button>
                );
              })}
            </div>
          ) : (
            <div data-testid="tests-list">
              <div className="flex items-center justify-between mb-5 max-w-3xl mx-auto">
                <button data-testid="change-pathway-btn" onClick={() => setSelectedPathway(null)} className="text-sm text-teal-600 hover:text-teal-700 inline-flex items-center font-medium">← Change Pathway</button>
                <Badge className={selectedPathway === 'btech' ? 'bg-teal-100 text-teal-700' : 'bg-blue-100 text-blue-700'}>
                  {testPathways.find(p => p.id === selectedPathway).title}
                </Badge>
              </div>
              <div className="space-y-3 max-w-3xl mx-auto">
                {journeyStages(selectedPathway).map((t) => {
                  const Icon = t.icon;
                  return (
                    <div key={t.id} data-testid={`stage-card-${t.id}`}
                      className="p-4 bg-white rounded-2xl border-2 border-gray-200 flex items-center gap-4">
                      <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 bg-teal-100">
                        <Icon className="text-teal-600" size={22} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-base md:text-lg font-bold text-gray-900 leading-snug">{t.title}</p>
                        <p className="text-xs md:text-sm text-gray-600 mb-1">{t.description}</p>
                        <div className="flex items-center gap-3 text-[11px] text-gray-500">
                          <span className="flex items-center gap-1"><Clock size={11} />{t.duration}</span>
                          <span>{t.questions} questions</span>
                        </div>
                      </div>
                      <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider hidden md:block">Step {t.id}</div>
                    </div>
                  );
                })}
              </div>
              <div className="text-center mt-6 max-w-3xl mx-auto">
                <Button asChild data-testid="start-journey-btn" className="bg-teal-600 hover:bg-teal-700 text-white shadow-md px-6 py-5 text-base">
                  <Link to={testPathways.find(p => p.id === selectedPathway).route}>
                    Start Career Clarity Journey <ArrowRight size={16} className="ml-2" />
                  </Link>
                </Button>
                <p className="text-xs text-gray-500 mt-3">After each test you&apos;ll see theory + AI awareness. Final report after Test 4.</p>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 px-4 md:px-6 bg-gradient-to-br from-teal-600 to-blue-600 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl md:text-4xl font-bold mb-6">
            Ready to make an informed decision?
          </h2>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" variant="secondary" className="text-lg">
              <Link to="/decision-report">View Decision Report</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="text-lg bg-white text-teal-600 hover:bg-gray-100">
              <Link to="/dashboard">Start exploring free</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default After10thPathway;
